/**
 * Dates as the app shows them. Every date arrives as a bare "YYYY-MM-DD"
 * calendar day, so it is parsed as LOCAL midnight: `new Date("2026-09-11")`
 * would be UTC midnight, which is the previous evening in Pacific time.
 */
export const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function day(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** "Fri, Sep 11 2026" */
export function fLong(iso: string): string {
  const d = day(iso);
  return `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
}

/** "Sep 11 2026" */
export function fMid(iso: string): string {
  const d = day(iso);
  return `${MON[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
}

/** "Oct 27 – 30 2022", "Aug 1 – Sep 2 2021", "Dec 30 2021 – Jan 2 2022"; one day when they match. */
export function fRange(first: string, last: string): string {
  if (first.slice(0, 10) === last.slice(0, 10)) return fMid(first);
  const a = day(first), b = day(last);
  if (a.getFullYear() !== b.getFullYear()) return `${fMid(first)} – ${fMid(last)}`;
  const end = a.getMonth() === b.getMonth() ? `${b.getDate()}` : `${MON[b.getMonth()]} ${b.getDate()}`;
  return `${MON[a.getMonth()]} ${a.getDate()} – ${end} ${a.getFullYear()}`;
}

/** "9.11.26" */
export function fShort(iso: string): string {
  const d = day(iso);
  return `${d.getMonth() + 1}.${d.getDate()}.${String(d.getFullYear()).slice(2)}`;
}

/** "September 2026" */
export function monthLabel(iso: string): string {
  const d = day(iso);
  return `${MONTH[d.getMonth()]} ${d.getFullYear()}`;
}

export const dow = (iso: string) => DOW[day(iso).getDay()].toUpperCase();
export const dayOfMonth = (iso: string) => day(iso).getDate();
export const yearOf = (iso: string) => Number(iso.slice(0, 4));

/** Today as a local "YYYY-MM-DD"; never toISOString(), which is the UTC day. */
export function todayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "1-3" style year span for a first/last pair; one year when they match. */
export function yearSpan(first: string | null, last: string | null): string {
  if (!first || !last) return "";
  const a = first.slice(0, 4), b = last.slice(0, 4);
  return a === b ? a : `${a}–${b}`;
}
